<script setup lang="ts">
import { UserState } from '@/composition/users'
import { VideoSource } from '@/helper/go.live.helper'
import { IPosts } from '@/models/Posts'
import { router } from '@/router'

import { PostState } from '@/composition/posts'
import { LiveStreamType } from '@/helper/go.live.helper'
import { PostType } from '@/models/Posts'
import { MediaService } from '@/service/MediaService'
import { useGlobalStore } from '@/store/app'
import {
  RoomControlBar,
  RoomProvider,
  RoomView,
} from '@ghl-revex/assets-drm-client'
import { UIModal } from '@gohighlevel/ghl-ui'
import { reactive, ref } from 'vue'
import { useRoute } from 'vue-router'
import { getUniqueMentions, trackMentions } from '../../mention-helper'
import BottomBar from './BottomBar.vue'
import TopBar from './TopBar.vue'

const route = useRoute()
const isLoading = ref(false)
const showTimer = ref(false)
const createdPost = ref<IPosts | null>(null)

const emit = defineEmits(['update:save', 'update:show'])

// Centralized form data
const formData = reactive({
  // Host Details
  selectedChannelId: '',
  liveTime: 'now',

  // Post Details
  title: '',
  description: '',
  shouldNotifyMembers: true,

  // Video Source
  videoSource: VideoSource.ROOM,
  streamKey: '',
  serverUrl: '',
  licenseId: '',
  sourceEntityId: '',
})

// Update function to be passed to child components
const updateFormData = (field: string, value: any) => {
  formData[field] = value
}

function handleCloseButton() {
  router.push({
    name: 'home',
    params: { groupSlug: route.params.groupSlug },
  })
}

function onStreamEnded() {
  console.log('stream ended')
}

const handleGoLive = async () => {
  // Create Post
  try {
    isLoading.value = true
    showTimer.value = true

    const { locationId, groupId, userId } = useGlobalStore().$state

    if (formData.videoSource === VideoSource.ROOM) {
      const assetLicense = await MediaService.createLicenseForAsset(groupId, {
        type: LiveStreamType.ROOM,
      })
      formData.licenseId = assetLicense.licenseId
      formData.sourceEntityId = assetLicense.sourceEntityId
    }
    const parser = new DOMParser()
    const content = parser.parseFromString(formData.description, 'text/html')

    const broadcastElements = content.querySelectorAll(
      'span[data-type="mention"][data-mention-type="broadcast"]'
    )
    let notifyAllGroupMembers = false
    if (broadcastElements.length > 0) {
      // notify all group memebrs
      notifyAllGroupMembers = true
    }
    const uniqueMentions = getUniqueMentions(content)
    trackMentions('post', uniqueMentions)

    let data: IPosts = {
      locationId: locationId,
      groupId: groupId as string,
      channelId: formData.selectedChannelId,
      title: formData.title,
      content: formData.description,
      mentions: uniqueMentions.user,
      channelMentions: uniqueMentions.channel,
      eventMentions: uniqueMentions.event,
      courseMentions: uniqueMentions.course,
      createdBy: userId,
      notifyAllGroupMembers: notifyAllGroupMembers,
      postType: PostType.GO_LIVE,
      metadata: {
        streamUrl: formData.serverUrl,
        streamKey: formData.streamKey,
        licenseId: formData.licenseId,
        sourceEntityId: formData.sourceEntityId,
        type: formData.videoSource as unknown as LiveStreamType,
        shouldSaveAsPost: true,
      },
    }

    const posts = await PostState.createPost(data, true)
    emit('update:show', posts)
    createdPost.value = posts
    isLoading.value = false

    // Navigate to the post view
    router.replace({
      name: 'group-post-view',
      params: {
        postId: posts._id,
      },
      query: {
        resetPostList: 'true',
      },
    })
  } catch (error) {
    console.error('Unable to create post', error)
    isLoading.value = false
  }
}
const onBack = () => {
  emit('update:show', false)
}
</script>

<template>
  <div class="live-post-view-container">
    <UIModal
      :show="true"
      :show-actions="false"
      :mask-closable="false"
      :close-on-esc="true"
      class-name="live-post-view-modal-container-mobile"
      title="Live Stream"
      @update:show="onStreamEnded"
    >
      <TopBar
        :handle-close-button="handleCloseButton"
        :form-data="formData"
        :update-form-data="updateFormData"
        :on-back="onBack"
        class="absolute w-full bg-transparent"
        :user="UserState.state.currentUser"
      />
      <RoomProvider role="host" class="flex">
        <RoomView
          ref="roomView"
          :participant-id="UserState.state.currentUser?.contactId"
          :participant-name="UserState.state.currentUser?.fullName"
          role="host"
          :license-id="formData.licenseId"
        >
          <template #controls>
            <RoomControlBar
              :controls="['camera', 'microphone']"
              :show-participants="false"
              :embedded-position="`right`"
            />
          </template>
        </RoomView>
      </RoomProvider>

      <BottomBar
        :form-data="formData"
        :update-form-data="updateFormData"
        :handle-go-live="handleGoLive"
        :is-loading="isLoading"
      />
    </UIModal>
  </div>
</template>
<style>
/* Full viewport modal styling */
.live-post-view-modal-container-mobile {
  margin: 0 !important;
  padding: 0 !important;
  height: 100vh !important; /* Dynamic viewport height for mobile */
  max-height: 100vh !important;
  width: 100vw !important;
  max-width: 100vw !important;
  border-radius: 0 !important;
  position: fixed !important;
  top: 0 !important;
  left: 0 !important;
  /* Safe area support for devices with notches */
  padding-top: env(safe-area-inset-top) !important;
  padding-bottom: env(safe-area-inset-bottom) !important;
  padding-left: env(safe-area-inset-left) !important;
  padding-right: env(safe-area-inset-right) !important;
}

/* Remove all padding from modal wrapper */
.live-post-view-modal-container-mobile .n-modal-body-wrapper {
  padding: 0 !important;
}

/* Remove padding from card container */
.live-post-view-modal-container-mobile.n-card,
.live-post-view-modal-container-mobile .n-card {
  padding: 0 !important;
  border-radius: 0 !important;
}

/* Remove padding from card content */
.live-post-view-modal-container-mobile .n-card__content {
  padding: 0 !important;
}

/* Remove padding from first card content child (header area) */
.live-post-view-modal-container-mobile .n-card__content:first-child {
  padding-top: 0 !important;
}

/* Remove padding from modal content wrapper */
.live-post-view-modal-container-mobile .n-modal-content {
  padding: 0 !important;
}

/* Ensure the modal body fills the entire space */
.live-post-view-modal-container-mobile .n-modal-body {
  padding: 0 !important;
  height: 100% !important;
}

/* Remove any box shadow or border */
.live-post-view-modal-container-mobile .n-modal {
  box-shadow: none !important;
  border: none !important;
}
</style>
<style scoped>
/* Main Container */
.live-container {
  width: 100%;
  height: 100dvh; /* Dynamic viewport height for mobile */
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

.live-layout {
  display: flex;
  width: 100%;
  height: 100%;
}

/* Video Section */
.video-section {
  flex: 1;
  background: #000000;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  position: relative;
}

.video-wrapper {
  width: 100%;
  height: 100%;
  max-width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

/* Chat Panel */
.chat-panel {
  width: 406px;
  min-width: 350px;
  max-width: 450px;
  background: #ffffff;
  display: flex;
  flex-direction: column;
  height: 100%;
  border-left: 1px solid #e5e7eb;
  box-shadow: -2px 0 8px rgba(0, 0, 0, 0.05);
}

/* Chat Header */
.chat-header {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background: #ffffff;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 12px;
}

.avatar-wrapper {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  flex-shrink: 0;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.avatar-placeholder {
  width: 100%;
  height: 100%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
}

.user-details {
  flex: 1;
  min-width: 0;
}

.live-status {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-name {
  font-size: 16px;
  font-weight: 600;
  color: #1d2939;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.live-indicator {
  width: 8px;
  height: 8px;
  background: #dc2626;
  border-radius: 50%;
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  flex-shrink: 0;
}

@keyframes pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}

.timestamp {
  font-size: 14px;
  color: #6b7280;
  margin: 2px 0 0 0;
}

.menu-button {
  padding: 8px;
  background: transparent;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  color: #6b7280;
  transition: all 0.2s;
  flex-shrink: 0;
}

.menu-button:hover {
  background: #f3f4f6;
}

/* Chat Container */
.chat-container {
  flex: 1;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fafbfc;
}

/* Make sure StreamChat fills the container */
:deep(.stream-chat) {
  height: 100% !important;
  display: flex !important;
  flex-direction: column !important;
}

:deep(.message-list) {
  flex: 1 !important;
  overflow-y: auto !important;
}

:deep(.input-container) {
  flex-shrink: 0 !important;
  margin: 0;
}

/* Error State */
.error-container {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #f9fafb;
}

.error-content {
  text-align: center;
  padding: 20px;
}

.error-icon {
  width: 64px;
  height: 64px;
  margin: 0 auto 16px;
  color: #9ca3af;
}

.error-title {
  font-size: 20px;
  font-weight: 600;
  color: #1f2937;
  margin: 0 0 8px 0;
}

.error-message {
  font-size: 14px;
  color: #6b7280;
  margin: 0;
}

/* Ensure video player maintains aspect ratio */
:deep(.stream-attendee-player) {
  width: 100% !important;
  height: 100% !important;
  display: flex !important;
  align-items: center !important;
  justify-content: center !important;
}

:deep(.stream-attendee-player video) {
  width: 100% !important;
  height: 100% !important;
  object-fit: cover !important;
  object-position: center !important;
}
/* Conversations css */

:deep(.message-input) {
  height: 22px !important;
  font-size: 16px !important;
  text-align: left !important;
  color: #fefeff !important;
  background: rgba(29, 41, 57, 0.32) !important;
}

:deep(.message-input::placeholder) {
  line-height: 1.2 !important;
  text-align: left !important;
  font-size: 16px !important;
  color: #fefeff !important;
}
:deep(.send-button) {
  height: 40px !important;
  width: 40px !important;
  font-size: 16px !important;
  background: #ffffff !important;
  border-radius: 8px !important;
  color: #333333 !important; /* This will change the SVG color */
}

/* Hide the existing SVG */
:deep(.send-button svg) {
  display: none !important;
}

/* Add custom send icon using CSS pseudo-element */
:deep(.send-button::after) {
  content: '' !important;
  display: block !important;
  width: 20px !important;
  height: 20px !important;
  background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23333333' stroke-width='2'%3E%3Cpath d='M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z'/%3E%3C/svg%3E") !important;
  background-size: contain !important;
  background-repeat: no-repeat !important;
  background-position: center !important;
}
:deep(.message-bubble.bubble-square) {
  border: none !important;
  padding-left: 0 !important;
}
:deep(.message-content) {
  flex: 1 !important;
}
:deep(.avatar-icon) {
  background: #ffffff !important;
  color: #333333 !important;
}
:deep(.status-bar) {
  top: 27px !important;
  right: 71px !important;
}
:deep(.video-container) {
  background: #909090 !important;
}
:deep(.stream-video) {
  background: #909090 !important;
}
</style>
